<template>
  <div class="p-4 space-y-4">
    <!-- Header Section -->
    <Card>
      <template #title>
        <div class="flex items-center space-x-4">
          <Avatar :label="initials" shape="circle" class="text-lg" />
          <div>
            <div class="text-xl font-bold">{{ person?.firstName }} {{ person?.lastName }}</div>
            <div v-if="person?.jobTitle || organization" class="text-sm text-gray-600">
              {{ person?.jobTitle }}<span v-if="person?.jobTitle && organization"> @ </span
              >{{ organization?.name }}
            </div>
            <div class="mt-1 space-x-2">
              <Tag v-for="tag in person?.tags ?? []" :key="tag" :value="tag" />
            </div>
          </div>
        </div>
      </template>
      <template #content>
        <div class="mt-4">
          <h3 class="text-lg font-semibold mb-2">Contact Details</h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><strong>Phone:</strong> {{ person?.phone ?? 'N/A' }}</div>
            <div><strong>Email:</strong> {{ person?.email ?? 'N/A' }}</div>
            <div><strong>Contact Type:</strong> {{ person?.type ?? 'N/A' }}</div>
            <div><strong>Address:</strong> {{ person?.streetAddress ?? 'N/A' }}</div>
          </div>
        </div>
      </template>
    </Card>

    <!-- Relationship Summary -->
    <Card>
      <template #title>Relationship Summary</template>
      <template #content>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div><strong>Stage:</strong> {{ person?.engagementStage ?? 'N/A' }}</div>
          <div>
            <strong>Last Transaction:</strong>
            {{ person?.lastTransactionDate ? formatDate(person.lastTransactionDate) : 'N/A' }}
          </div>
          <div><strong>Transactions:</strong> {{ transactionCount }}</div>
          <div><strong>Notes:</strong> {{ lastNote ?? 'N/A' }}</div>
          <div><strong>Created By:</strong> {{ ownerName ?? 'N/A' }}</div>
        </div>
      </template>
    </Card>

    <!-- Action Buttons -->
    <Card>
      <template #title>Quick Actions</template>
      <template #content>
        <div class="flex flex-wrap gap-2">
          <Button
            label="Call"
            icon="pi pi-phone"
            severity="success"
            @click="callPerson"
            :disabled="!person?.phone"
          />
          <Button
            label="Email"
            icon="pi pi-envelope"
            severity="info"
            @click="emailPerson"
            :disabled="!person?.email"
          />
          <Button label="Log Note" icon="pi pi-plus" severity="secondary" @click="logNote" />
          <Button
            label="Schedule"
            icon="pi pi-calendar"
            severity="help"
            @click="scheduleFollowup"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef, ref, watchEffect } from 'vue'
import { useTransactionStore } from '@/stores/transactionStoreOld'
import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStore } from '@/stores/v2/organization'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Tag from 'primevue/tag'
import Avatar from 'primevue/avatar'
import { formatDate } from '@/utils/formatters'
import { useCallLogger } from '@/composables/useCallLogger'
import { usePhoneService } from '@/services/phoneService'
import type { Person } from '@/types/Person'
import type { Organization } from '@/types/Organization'

const props = defineProps<{
  personId: string
}>()

const personId = toRef(props, 'personId')
const personStore = usePersonStore()
const organizationStore = useOrganizationStore()
const transactionStore = useTransactionStore()
const { openLogger } = useCallLogger()
const phoneService = usePhoneService()

// Load person data
const person = ref<Person | null>(null)
const organization = ref<Organization | null>(null)

watchEffect(async () => {
  if (personId.value) {
    const personData = await personStore.get(personId.value)
    person.value = personData || null
    if (personData?.organizationId) {
      const orgData = await organizationStore.get(personData.organizationId)
      organization.value = orgData || null
    }
  }
})

const transactionCount = ref<number | null>(null)
watchEffect(async () => {
  if (person.value?.id) {
    transactionCount.value = await transactionStore.countByPersonId(person.value.id)
  }
})

const lastNote = computed(() => person.value?.notes)
const ownerName = computed(() => person.value?.createdBy)

const initials = computed(() => {
  if (!person.value) return ''
  return (
    (person.value.firstName?.charAt(0) ?? '') + (person.value.lastName?.charAt(0) ?? '')
  ).toUpperCase()
})

function callPerson() {
  if (person.value?.phone) {
    const callUrl = phoneService.getCallUrl(person.value.phone)

    // Create a temporary link element for better mobile compatibility
    const link = document.createElement('a')
    link.href = callUrl
    link.target = '_self'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
  openLogger({ personId: person.value?.id })
}

function emailPerson() {
  if (person.value?.email) {
    window.open(`mailto:${person.value.email}`)
  }
}

function logNote() {
  // open dialog or navigate to logging screen
}

function scheduleFollowup() {
  // open calendar modal or create follow-up task
}
</script>

<style scoped>
.text-sm strong {
  font-weight: 600;
  margin-right: 0.25rem;
}
.p-card {
  margin-bottom: 1rem;
}
</style>
